/**
 * 숫자값을 문자열로 변환한다.
 * - null, undefined, 빈 문자열인 경우 빈 문자열 반환
 * - 문자열 입력 시 콤마(,) 제거 후 숫자로 변환
 * - 숫자로 변환할 수 없는 경우 빈 문자열 반환
 *
 * @param value 숫자 또는 숫자 형태의 문자열
 * @returns 숫자를 문자열로 변환한 값
 *
 * @example
 * formatNumber(1000);  // "1000"
 * formatNumber(1,000); // "1000"
 * formatNumber("");    // ""
 * formatNumber(null);  // ""
 * formatNumber("abc"); // ""
 */
export const formatNumber = (value: number | string | null | undefined): string => {
  if (value == null || value === undefined || value === '') {
    return '';
  }

  const num = typeof value === 'string' ? Number(value.replace(/,/g, '')) : value;
  if (Number.isNaN(num)) return '';
  return num.toString();
};

/**
 * 문자열 형태의 숫자를 number 타입으로  파싱한다.
 *
 * - 콤마(,) 제거 후 Number 변환
 * - 변환 실패 시 null 반환
 * @param value 숫자 문자열
 * @returns number 타입 또는 null
 *
 * @example
 * parseNumber("1,000");    // 1000
 * parseNumber("123");      // 123
 * parseNumber("");         // null
 * parseNumber("abc");      // null
 */
export const parseNumber = (value: string): number | null => {
  if (!value) return null;
  const num = Number(value.replace(/,/g, ''));
  return Number.isNaN(num) ? null : num;
};

/**
 * 통화 값을 로케일 기준으로 통화 문자열로 포맷한다.
 *
 * - 기본 통화는 KRW
 * - KRW 소수점 미표시
 * - USD는 소수점 2자리 표시
 *
 * @param value 금액
 * @param currency 통화코드 ('KRW' | 'USD')
 * @returns 통화 포맷 문자열
 *
 * @example
 * formatCurrency(1000);        // "1,000";
 * formatCurrency(1000, 'KRW'); // "1,000";
 * formatCurrency(1000, 'USD'); // "1,000.00";
 */
export const formatCurrency = (value: number, currency: 'KRW' | 'USD' = 'KRW') => {
  return new Intl.NumberFormat('ko-KR', {
    style: 'currency',
    currency,
    minimumFractionDigits: currency === 'KRW' ? 0 : 2,
  }).format(value);
};

/**
 * 문자열을 Comma를 찍어 준다.
 * @param s
 * @returns
 * @example
 * getCommaNumber('1000') => 1,000 출력
 */
export const getCommaNumber = (s: string) => {
  //s = trim(s);
  if (s == null || s == '') return '0';
  if (s == '0') return s;
  var str = s.replace(/\D/g, '');
  var len = str.length;
  var tmp = '';
  var tm2 = '';
  var i = 0;
  while (str.charAt(i) == '0') i++;
  str = str.substring(i, len);
  len = str.length;
  if (len < 3) {
    return str;
  } else {
    var sit = len % 3;
    if (sit > 0) {
      tmp = tmp + str.substring(0, sit) + ',';
      len = len - sit;
    }
    while (len > 3) {
      tmp = tmp + str.substring(sit, sit + 3) + ',';
      len = len - 3;
      sit = sit + 3;
    }
    tmp = tmp + str.substring(sit, sit + 3) + tm2;
    str = tmp;
  }
  return str;
};

/**
 * 문자열을 format형태로 찍어 준다.
 * @param number
 * @param numberType
 * @param format
 * @returns
 * @example
 * getNumberFormat('100000', 1, '#,#00') => 100,000 출력.
 * getNumberFormat('100000', 1, '#,#00.00') => 1,000.00 출력.
 * getNumberFormat('100000', 10, '#,#00.00') => 100.00 출력.
 */
export const getNumberFormat = (number: string, numberType: number, format: string) => {
  if (number == null || number == '') {
    number = '';
    return number;
  }
  var sign = '';
  var num1 = '';
  var num2 = '';
  var fixedLen = 0;
  var floorLen = 0;
  var tempChk = ''; // 19.03.13 1자리를 받을 경우 이슈 사항으로 인한 추가
  var temp = '';

  if (numberType > 1) floorLen = new String(numberType).length - 1;

  if (number.indexOf('-') != -1) {
    sign = '-';
  }

  if (number != '0') {
    number = number.substring(0, number.length - floorLen);

    if (format != '' && format.indexOf('.') > -1) {
      temp = format.substring(format.indexOf('.') + 1);
      fixedLen = temp.length;
      if (temp.indexOf('00') > -1) {
        tempChk = 'Y';
      }
    }

    num1 = getCommaNumber(number.substring(0, number.length - fixedLen));
    // 19.03.13 1자리를 받을 경우 이슈 사항으로 인한 추가
    if (tempChk == 'Y' && number.length == 1) {
      num2 = '0' + number.substring(number.length - fixedLen);
    } else {
      num2 = number.substring(number.length - fixedLen);
    }
  } else {
    num1 = '0';
    if (format != '' && format.indexOf('.') > -1) {
      temp = format.substring(format.indexOf('.') + 1);
      fixedLen = temp.length;
    }
    for (var i = 0; i < fixedLen; i++) {
      num2 += '0';
    }
  }

  if (fixedLen != 0) {
    return sign + num1 + '.' + num2;
  } else {
    return sign + num1;
  }
};

/**
 * 펀드 이율 문자열을 퍼센트(%) 형식으로 변환한다.
 *
 * - 입력 문자열 길이에 따라 소수점 위치를 자동 조정
 * - 예:
 *  - "12345" -> "123.45%"
 *  - "1234" -> "12.34%"
 *  - "12" -> "0.12%"
 *  - "1" -> "0.01%"
 *
 * @param str 숫자로 구성된 이율 문자열
 * @returns 퍼센트 문자열
 */
export const getFundRate = (str: string) => {
  var len = str.length;
  var returnValue = '';
  if (len == 7) {
    returnValue = str.substring(0, 5) + '.' + str.substring(5, 7) + '%';
  } else if (len == 6) {
    returnValue = str.substring(0, 4) + '.' + str.substring(4, 6) + '%';
  } else if (len == 5) {
    returnValue = str.substring(0, 3) + '.' + str.substring(3, 5) + '%';
  } else if (len == 4) {
    returnValue = str.substring(0, 2) + '.' + str.substring(2, 4) + '%';
  } else if (len == 3) {
    returnValue = str.substring(0, 1) + '.' + str.substring(1, 3) + '%';
  } else if (len == 2) {
    returnValue = '0.' + str + '%';
  } else if (len == 1) {
    returnValue = '0.0' + str + '%';
  } else {
    returnValue = '0%';
  }
  return returnValue;
};

/**
 * 문자열 내 특정 문자열(oldStr)을 모두(newStr)로 치환한다.
 * @param source 변경대상 문자열
 * @param oldStr 변경 전 문자
 * @param newStr 변경 후 문자
 * @returns 치환된 문자열
 * @example
 * replaceAll('1,234,567', ',', '');    // '1234567'
 * replaceAll('a-b-c', '-', '_');       // 'a_b_c'
 */
export const replaceAll = (source: string, oldStr: string, newStr: string) => {
  const re = new RegExp(oldStr, 'g');
  source = source.replace(re, newStr);
  return source;
};

/**
 * 문자열에서 콤마(,) 를 제거한다.
 *
 * @param {String} numString 문자열
 * @returns 콤마 제거된 문자열
 * @example
 * stripCommas('1,234');    // '1234'
 */
export const stripCommas = (numString: string) => {
  return numString ? replaceAll(numString, ',', '') : '';
};

/**
 * 문자열에서 html 태그 및 script/style/comment 등을 제거한다.
 *
 * @param str 문자열
 * @returns html/태그가 제거된 문자열
 * @example
 * stripTags('<b>Hello</b>');                   // 'Hello'
 * stripTags('<script>alert(1)</script>OK');    // 'OK'
 */
export const stripTags = (str: string): string => {
  var RegExpTag = /[<][^>]*[>]/gi;
  str = str.replace(RegExpTag, '');

  var RegExpJS = '<script[^>]*>(.*?)</script>';
  str = str.replace(RegExpJS, '');

  var RegExpCSS = '<style[^>]*>(.*?)';
  str = str.replace(RegExpCSS, '');

  var RegExpDS = /<!--[^>](.*?)-->/gi;
  str = str.replace(RegExpDS, '');

  var RegExpPh = /document.|object|cookie|&/gi;
  str = str.replace(RegExpPh, '');

  return str;
};

/**
 * 계좌번호 하이픈 추가.
 * @param num 계좌번호
 * @param se 계좌번호 구분값 (기본: 하이푼(-))
 * @returns
 * @example
 * formatHyphen('123-45 678901');   //'123-45-678901'
 * formatHyphen('');                // ''
 */
export const formatHyphen = (num: string, se: string = '-') => {
  if (num && num != '') {
    num = num.replace(/[^\d]/g, '');
    se = se || '-';

    if (num.length == 11) {
      // 3,2,6
      num = num.replace(/([0-9]{3})([0-9]{2})([0-9]+)/, '$1' + se + '$2' + se + '$3');
    }

    return num;
  } else {
    return '';
  }
};

/**
 * 숫자문자열에 천단위 콤마를 추가한다.
 * (문자열 내 숫자만 남기고 콤마 포매팅)
 *
 * @param numString 숫자 문자열
 * @returns 콤마가 포함된 문자열
 * @example
 * formatCommas('1234567');     // '1,234,567'
 * formatCommas('-1234567');    // '-1,234,567'
 * formatCommas('');            // ''
 */
export const formatCommas = (numString: string) => {
  if (!numString) return '';

  numString = numString + '';

  var re = /,|\s+/g;
  var retNumString = numString.replace(re, '');

  re = /(-?\d+)(\d{3})/;
  while (re.test(retNumString)) retNumString = retNumString.replace(re, '$1,$2');

  return retNumString;
};

/**
 * 문자열에서 숫자만 추출하여 반환한다.
 * - 첫 글자가 '-'이면 음수로 판단하여 '-'를 유지한다.
 *
 * @param numStr 숫자 문자열(하이픈/공백/기타 문자 포함 가능)
 * @returns 숫자만 남긴 문자열(음수 부호 유지 가능)
 * @example
 * toNumber('1,234');       // '1234'
 * toNumber('-12,345');     // '-12345'
 * toNumber('abc');         // ''
 */
export const toNumber = (numStr: string): string => {
  if (!numStr) return '';

  numStr = numStr + '';

  var m = numStr.substring(0, 1) == '-';
  var ret = numStr.replace(/[^\d]/g, '');

  return m ? '-' + ret : ret;
};

/**
 * 숫자 문자열을 소수점 자리수(point) 기준으로 버림 처리하여 number로 변환한다.
 * - 콤마 제거 후 Number 변환
 * - Nan이면 0 반환
 *
 * @param numStr 숫자 문자열
 * @param point 소숫점 자릿수(기본 2)
 * @returns 버림 처리된 number
 *
 * @example
 * toFloat('123.456', 2);       // 123.45
 * toFloat('1,234.567', 1);     // 1234.5
 * toFloat('abc');              // 0
 */
export const toFloat = (value: string, point: number = 2) => {
  if (!value) {
    return 0;
  }

  const num = Number(value.replace(/,/g, ''));
  if (isNaN(num)) {
    return 0;
  }

  const factor = Math.pow(10, point);
  return Math.trunc(num * factor) / factor;
};

/**
 * 날짜+시간 문자열을 YYYY-MM-DD HH:mm:ss  형태로 변환한다.
 * - 입력에서 숫자만 추출한 뒤 앞 8자=날짜, 뒤=시간으로 처리
 *
 *
 * @param dateStr 날짜시간 문자열(예: "20251231123501", 2025-12-31 123501)
 * @param se 날짜 구분자 (기본 -)
 * @returns 포맷된 날짜 문자열
 *
 * @example
 * toDateTime('20251231123501');    // '2025-12-31 12:35:01'
 * toDateTime('2025-12-31 1235');   // '2025-12-31 12:35'
 */
export const toDateTime = (dateStr: string, se: string = '-') => {
  const dateStrNoDash = toNumber(dateStr);
  var date = toDate(dateStrNoDash.substring(0, 8), se);
  var time = toTime(dateStrNoDash.substring(8), ':');
  return date + ' ' + time;
};

/**
 * 주민번호 형식으로 변환.
 *
 * @param num 주민번호 13자리 문자열
 * @returns 포맷된 주민번호
 *
 * @example
 * toRrno('123456XXXXXXX'); // '123456-XXXXXXX'
 */
export const toRrno = (num: string): string => {
  if (num) return num.replace(/[^\d]/g, '').replace(/(\d{6})(\d+)/g, '$1-$2');
  else return '';
};

/**
 * 전화번호 형식으로 변환.
 *
 * @param num 전화번호 문자열
 * @param se 구분자 (기본 '-')
 * @returns 포맷된 전화번호
 *
 * @example
 * toPhone('01012345678');          // '010-1234-5678'
 * toPhone('0101235678');           // '010-123-5678'
 * toPhone('01012345678', '.');     // '010.1234.5678'
 */
export const toPhone = (num: string, se: string = '-') => {
  se = se || '-';

  if (num)
    return num
      .replace(/[^\d]/g, '')
      .replace(/(^02.{0}|^01.{1}|[0-9]{3})([0-9]+)([0-9]{4})/, '$1' + se + '$2' + se + '$3');
  else return '';
};

/**
 * 계좌번호 형식으로 변환
 * @param bank 은행코드
 * @param num 계좌번호
 * @param se 구분자 (기본값: '-')
 * @returns 포맷된 계좌번호
 *
 * @example
 * toAccount('23', '123456789012345');   // '123-45-678901-2345' (길이 15 케이스)
 * toAccount('23', '1234567890123');     // '123-4567-8901-23' (길이 13 케이스)
 */
export const toAccount = (bank: string, num: string, se: string = '-') => {
  if (num && num != '') {
    num = num.replace(/[^\d]/g, '');
    se = se || '-';
    bank = bank && bank.length == 3 ? bank.substring(1) : bank;

    if (!bank) return num;
    if (bank != '23' && bank != 'SC제일은행') return num;

    if (num.length == 15) {
      // 3,2,6,4
      num = num.replace(
        /([0-9]{3})([0-9]{2})([0-9]{6})([0-9]+)/,
        '$1' + se + '$2' + se + '$3' + se + '$4',
      );
    } else if (num.length == 13) {
      // 3,4,4,2
      num = num.replace(
        /([0-9]{3})([0-9]{4})([0-9]{4})([0-9]+)/,
        '$1' + se + '$2' + se + '$3' + se + '$4',
      );
    } else if (num.length == 12) {
      // 3,2,6,1
      num = num.replace(
        /([0-9]{3})([0-9]{2})([0-9]{6})([0-9]+)/,
        '$1' + se + '$2' + se + '$3' + se + '$4',
      );
    } else if (num.length == 11) {
      // 3,2,6
      num = num.replace(/([0-9]{3})([0-9]{2})([0-9]+)/, '$1' + se + '$2' + se + '$3');
    } else if (num.length == 10) {
      // 3,3,4
      num = num.replace(/([0-9]{3})([0-9]{3})([0-9]+)/, '$1' + se + '$2' + se + '$3');
    } else if (num.length == 9) {
      // 4,4,1
      num = num.replace(/([0-9]{4})([0-9]{4})([0-9]+)/, '$1' + se + '$2' + se + '$3');
    } else if (num.length == 8) {
      // 4,4
      num = num.replace(/([0-9]{4})([0-9]+)/, '$1' + se + '$2');
    }
    return num;
  } else return '';
};

/**
 * 계좌번호 포맷팅 처리, 옵션에 따라 마스킹('*') 처리한다.
 *
 * @param bank 은행코드
 * @param num 계좌번호
 * @param se 구분자
 * @param pMaskYn 마스킹여부 (Y/N) 기본 'Y'
 * @returns 포맷/마스킹된 계좌번호
 *
 * @example
 * toAccount('23', '12345678912345', '-', 'Y');   // '***********2345'
 * toAccount('23', '12345678912345', '-', 'N');   // '123-45-678901-2345'
 */
export const toAccNo = (bank: string, num: string, se: string = '-', pMaskYn: string = 'Y') => {
  if (num && num != '') {
    num = num.replace(/[^\d]/g, '');
    se = se || '';
    bank = bank && bank.length == 3 ? bank.substring(1) : bank;

    if (!bank) return num;
    if (pMaskYn == 'Y') {
      var viewCount = num.length - 6;
      if (viewCount > 0) {
        num = num.substring(0, viewCount).padEnd(num.length, '*');
      }
      return num;
    } else {
    }

    if (num.length == 15) {
      // 3,2,6,4
      num = num.replace(
        /([0-9]{3})([0-9]{2})([0-9]{6})([0-9]+)/,
        '$1' + se + '$2' + se + '$3' + se + '$4',
      );
    } else if (num.length == 13) {
      // 3,4,4,2
      num = num.replace(
        /([0-9]{3})([0-9]{4})([0-9]{4})([0-9]+)/,
        '$1' + se + '$2' + se + '$3' + se + '$4',
      );
    } else if (num.length == 12) {
      // 3,2,6,1
      num = num.replace(
        /([0-9]{3})([0-9]{2})([0-9]{6})([0-9]+)/,
        '$1' + se + '$2' + se + '$3' + se + '$4',
      );
    } else if (num.length == 11) {
      // 3,2,6
      num = num.replace(/([0-9]{3})([0-9]{2})([0-9]+)/, '$1' + se + '$2' + se + '$3');
    } else if (num.length == 10) {
      // 3,3,4
      num = num.replace(/([0-9]{3})([0-9]{3})([0-9]+)/, '$1' + se + '$2' + se + '$3');
    } else if (num.length == 9) {
      // 4,4,1
      num = num.replace(/([0-9]{4})([0-9]{4})([0-9]+)/, '$1' + se + '$2' + se + '$3');
    } else if (num.length == 8) {
      // 4,4
      num = num.replace(/([0-9]{4})([0-9]+)/, '$1' + se + '$2');
    }
    return num;
  } else return '';
};

/**
 * 외환 계좌번호 형식화
 * @param num 외환계좌번호
 * @param se 구분자 (기본값 '-')
 * @return 형식화된 계좌번호
 *
 * @example
 * toFexAccount('12345678912345');  // '123-45-6789012345'
 */
export const toFexAccount = (num: string, se: string = '-') => {
  if (num && num != '') {
    num = num.replace(/[^\d]/g, '');
    se = se || '-';

    if (num.length == 15) {
      // 3,2,10
      num = num.replace(/([0-9]{3})([0-9]{2})([0-9]+)/, '$1' + se + '$2' + se + '$3');
    } else if (num.length == 13) {
      // 3,4,4,2
      num = num.replace(
        /([0-9]{3})([0-9]{4})([0-9]{4})([0-9]+)/,
        '$1' + se + '$2' + se + '$3' + se + '$4',
      );
    }

    return num;
  } else {
    return '';
  }
};

/**
 * 사업자번호 형식으로 변환
 * @param num 사업자번호
 * @se (선택)구분문자. 기본값 "-"
 * @return 형식화된 사업자번호
 *
 * @example
 * toBizNo('1234512345');   //123-45-12345
 */
export const toBizNo = (num: string, se: string = '-') => {
  if (num && num != '') {
    num = num.replace(/[^\d]/g, '');
    se = se || '-';

    if (num.length == 10) {
      // 3,2,5
      num = num.replace(/([0-9]{3})([0-9]{2})([0-9]+)/, '$1' + se + '$2' + se + '$3');
    }

    return num;
  } else {
    return '';
  }
};

/**
 * 카드번호 형식으로 변환.
 *
 * @param num 카드번호 문자열
 * @param se 구분자 (기본 '-')
 * @returns 포맷된 카드번호 문자열
 *
 * @example
 * toCard('1234123412341234);   // '1234-1234-1234-1234'
 */
export const toCard = (num: string, se: string = '-'): string => {
  se = se || '-';

  if (num)
    return num
      .replace(/[^\d]/g, '')
      .replace(/([0-9]{4})([0-9]{4})([0-9]{4})([0-9]+)/, '$1' + se + '$2' + se + '$3' + se + '$4');
  else return '';
};

/**
 * 날짜형식으로 변환.
 *
 * @param dateStr 날짜문자열
 * @param se 구분자
 * @returns 포맷된 날짜 문자열
 *
 * @example
 * toDate('20251231', '-');     // '2025-12-31'
 * toDate('202512', '-');       // '2025-12'
 * toDate('2025', '-');         // '2025'
 */
export const toDate = (dateStr: string, se: string) => {
  let re: RegExp;
  let replace = '';

  var dateStrNoDash = toNumber(dateStr);

  if (dateStrNoDash.length == 4) {
    re = /(\d{4})/;
    replace = '$1';
  } else if (dateStrNoDash.length == 5) {
    re = /(\d{4})/;
    replace = '$1' + se;
  } else if (dateStrNoDash.length == 6) {
    re = /(\d{4})(\d{2})/;
    replace = '$1' + se + '$2';
  } else if (dateStrNoDash.length == 7) {
    re = /(\d{4})(\d{2})/;
    replace = '$1' + se + '$2' + se;
  } else {
    re = /(\d{4})(\d{2})(\d{2})/;
    replace = '$1' + se + '$2' + se + '$3';
  }

  return dateStrNoDash.replace(re, replace);
};

type LangType = {
  [locale: string]: string;
};

/**
 * 날짜형식으로 변환.(년,월,일)
 * @param dateStr 날짜 문자열
 * @param locale 로케일 (기본값: ko)
 * @returns 로케일 반영된 날짜 문자열
 *
 * @example
 * toDateType2('20251231', 'ko');   // '2025년12월31일'
 * toDateType2('20251231', 'en');   // '2025Year12Month31Day'
 */
export const toDateType2 = (dateStr: string, locale: string = 'ko') => {
  let re: RegExp;
  let replace = '';

  const yearLang: LangType = {
    ko: '년',
    en: 'Year',
  };

  const monthLang: LangType = {
    ko: '월',
    en: 'Month',
  };

  const dayLang: LangType = {
    ko: '일',
    en: 'Day',
  };

  const dateStrNoDash = toNumber(dateStr);

  if (dateStrNoDash.length == 4) {
    re = /(\d{4})/;
    replace = '$1' + yearLang[locale];
  } else if (dateStrNoDash.length == 5) {
    re = /(\d{4})/;
    replace = '$1' + yearLang[locale];
  } else if (dateStrNoDash.length == 6) {
    re = /(\d{4})(\d{2})/;
    replace = '$1' + yearLang[locale] + '$2' + monthLang[locale];
  } else if (dateStrNoDash.length == 7) {
    re = /(\d{4})(\d{2})/;
    replace = '$1' + yearLang[locale] + '$2' + monthLang[locale];
  } else {
    re = /(\d{4})(\d{2})(\d{2})/;
    replace = '$1' + yearLang[locale] + '$2' + monthLang[locale] + '$3' + dayLang[locale];
  }

  return dateStrNoDash.replace(re, replace);
};

/**
 * 시분초 현식으로 변환.
 *
 * @param time 시간문자열
 * @param se 구분자
 * @returns 포맷된 시간 문자열 (유효하지 않으면 공백)
 *
 * @example
 * toTime('123059', ':');       // '12:30:59'
 * toTime('0830', ':');         // '08:30'
 */
export const toTime = (time: string, se: string) => {
  if (time)
    if (time.length == 6)
      return toNumber(time).replace(/(\d{2})(\d{2})(\d{2})/, '$1' + se + '$2' + se + '$3');
    else
      return toNumber(time).replace(
        /(\d{2})(\d{2})(\d{2})(\d{3})/,
        '$1' + se + '$2' + se + '$3' + '.' + '$4',
      );
  else return '';
};

/**
 * 금액 문자열을 콤마가 포함된 표준 금액 문자열로 변환한다.
 *
 * - 음수 유지
 * - 숫자만 추출 후 formatCommas 적용
 * @param numStr 금액 문자열
 * @returns 콤마가 포함된 금액 문자열
 *
 * @example
 * toMoney('1234567');      // '1,234,567'
 * toMoney('-1234567');     // '-1,234,567'
 * toMoney('1,234원');      // '-1,234'
 */
export const toMoney = (numStr: string): string => {
  if (!numStr) return '';
  numStr = numStr.toString();

  const minus: boolean = numStr.substring(0, 1) === '-';
  numStr = numStr.replace(/[^\d]/g, '');
  numStr = Number(numStr) + '';
  numStr = formatCommas(numStr);

  return minus ? '-' + numStr : numStr;
};

/**
 * 금액 형식으로 변환.
 *
 * @param numStr 금액 문자열
 */
export const toDon = (numStr: string): string => {
  if (!numStr) return '';
  numStr = numStr.toString();

  const minus: boolean = numStr.substring(0, 1) === '-';
  numStr = numStr.replace(/[^\d]/g, '');
  numStr = Number(numStr) + '';
  numStr = formatHyphen(numStr);

  return minus ? '-' + numStr : numStr;
};

/**
 * 표시용 이율/환율 문자열을 길이/타입에 따라 % 형태로 변환
 *
 * @param str 숫자 문장려
 * @param type 표시타입
 * @returns 퍼센트 문자열
 *
 * @example
 * DISPLAY_RATE('12345');       // '123.45%'
 * DISPLAY_RATE('1234', 0);   // '12.34%'
 */
export const DISPLAY_RATE = (str: string, type: number = 0) => {
  const len = str.length;
  let returnValue = '';
  if (len == 7) {
    returnValue = str.substring(0, 2) + '.' + str.substring(2, 4) + '0%';
  } else if (len == 6) {
    returnValue = str.substring(0, 1) + '.' + str.substring(1, 3) + '0%';
  } else if (len == 5) {
    returnValue = str.substring(0, 2) + '.' + str.substring(2, 3) + '0%';
  } else if (len == 4) {
    if (type == 0) {
      returnValue = str.substring(0, 1) + '.' + str.substring(1, 2) + '00%';
    } else if (type == 1) {
      returnValue = str.substring(0, 1) + '.' + str.substring(1, 3) + '0%';
    }
  } else if (len == 3) {
    returnValue = '0.' + str + '%';
  } else if (len == 2) {
    returnValue = '0.0' + str + '%';
  } else if (len == 1) {
    returnValue = '0.00' + str + '%';
  } else {
    returnValue = '0%';
  }
  return returnValue;
};

/**
 * 대출 이율 소수점 2자리만 표현
 * @param str
 * @param type
 * @returns
 */
export const DISPLAY_RATE2 = (str: string, type: number = 0) => {
  var len = str.length;
  var returnValue = '';
  if (len == 5) {
    returnValue = str.substring(0, 2) + '.' + str.substring(2, 3) + '0';
  } else if (len == 4) {
    if (type == 0) {
      returnValue = str.substring(0, 1) + '.' + str.substring(1, 2) + '00';
    } else if (type == 1) {
      returnValue = str.substring(0, 1) + '.' + str.substring(1, 3) + '0';
    }
  } else if (len == 3) {
    returnValue = '0.' + str;
  } else if (len == 2) {
    returnValue = '0.0' + str;
  } else if (len == 1) {
    returnValue = '0.00' + str;
  } else {
    returnValue = '0';
  }
  return Number(returnValue).toFixed(2) + '%';
};

/**
 * 대출이율 표시 포맷(###.00%) 헝태로 변환한다.
 *
 * @param str 숫자 문자열
 * @returns 퍼센트 문자열
 *
 * @example
 * DISPLAY_LOAN_RATE('1234');       // '12.34%'
 * DISPLAY_LOAN_RATE('0');          // '0%'
 */
export const DISPLAY_LOAN_RATE = (str: string) => {
  const len: number = str.length;
  let returnValue = '';
  if (len == 4) {
    returnValue = str.substring(0, 2) + '.' + str.substring(2, 4) + '%';
  } else if (len == 3) {
    returnValue = str.substring(0, 1) + '.' + str.substring(1, 3) + '%';
  } else if (len == 2) {
    returnValue = '0.' + str.substring(0, 2) + '%';
  } else if (len == 1 && str != '0') {
    returnValue = '0.0' + str.substring(0, 2) + '%';
  } else {
    returnValue = '0%';
  }
  return returnValue;
};

/**
 * 대출이율 표시 포맷2(###.00%)
 *
 * @param str 숫자 문자열
 * @param cnt 소수점 자리수(뒤에서 cnt자리)
 * @returns 퍼센트 문자열
 *
 * @example
 * DISPLAY_LOAN_RATE2('12345', 2);      // '123.45%'
 * DISPLAY_LOAN_RATE2('12345', 3);      // '12.345%'
 */
export const DISPLAY_LOAN_RATE2 = (str: string, cnt: number) => {
  let result: string = '0';
  const len: number = str.length;

  if (len > cnt) {
    result = str.substring(0, len - cnt) + '.' + str.substring(len - cnt);
  }

  return result + '%';
};

/**
 * 외환 이율/환율 표시용 포맷 함수 (###.00%)
 *
 * @param str 숫자 문자열
 * @returns 표시 문자열
 *
 * @example
 * DISPLAY_FOREIGN_RATE('0');       // '0.00'
 * DISPLAY_FOREIGN_RATE('12345');       // '123.45%'
 */
export const DISPLAY_FOREIGN_RATE = (str: string = '0') => {
  const len = str.length;
  let returnValue = '';
  if (str === '0') {
    returnValue = '0.00';
  } else {
    if (len == 7) {
      returnValue = str.substring(0, 3) + '.' + str.substring(5, 7) + '%';
    } else if (len == 6) {
      returnValue = str.substring(0, 2) + '.' + str.substring(4, 6) + '%';
    } else if (len == 5) {
      returnValue = str.substring(0, 1) + '.' + str.substring(3, 5) + '%';
    } else if (len == 4) {
      returnValue = '0.' + str.substring(0, 2) + '%';
    } else if (len == 3) {
      returnValue = '0.0' + str.substring(0, 1) + '%';
    } else if (len == 2) {
      returnValue = '0.00' + str.substring(0, 1) + '%';
    } else if (len == 1) {
      returnValue = '0.000' + str + '%';
    } else {
      returnValue = '0%';
    }
  }
  return returnValue;
};

/**
 * 금액을 #.## 형식으로 표현
 * @param s
 * @returns
 */
export const A_DISPLAY_NUMBER = (s: string) => {
  var str = s.trim();
  var Itg = ''; // 정수
  var point = ''; // 소수점

  if (str != null && str != '') {
    if (str.indexOf('.') != -1) {
      // s값의 . 있을시
      Itg = str.substring(0, str.indexOf('.'));
      point = str.substring(str.indexOf('.'), str.length);

      if (point.length == 2) {
        point += '0';
      }

      return toMoney(Itg) + point.substring(0, 3);
    } else {
      // 없을시
      return toMoney(str);
    }
  } else {
    return '0';
  }
};

/**
 * 소수점 표시 금액 포멧
 * @param value 금액 (문자열 또는 숫자)
 * @param pos 소수점 자리수(기본 2)
 * @returns 로케일 금액 문자열
 *
 * @example
 * toFebMoney(12345)        //12,345.00
 * toFebMoney(12345.12, 0)  //12,345
 * toFebMoney(12345.12, 4)  //12,345.1200
 */
export const toFebMoney = (value: string | number, pos: number = 2): string => {
  if (value === null || value === undefined || value === '') {
    return '0';
  }

  const num = Number(typeof value === 'string' ? value.replace(/,/g, '') : value);

  if (Number.isNaN(num)) return '0';

  return num.toLocaleString('ko-KR', {
    minimumFractionDigits: pos,
    maximumFractionDigits: pos,
  });
};

enum JongsungType {
  INDETERMINATE = 0,
  NO = 1,
  YES = 2,
}
/**
 * 종성 여부 체크.
 *
 * @param {String} wd
 */

/**
 * 종성 여부 체크.
 *
 * @param wd 한글 문자열
 * @returns 종성 없음: 1, 종성있음: 2
 */
export const isJongsong = (wd: string): number => {
  if (!wd) {
    return JongsungType.INDETERMINATE;
  }

  const lastChar = wd[wd.length - 1];
  const code = lastChar.charCodeAt(0);
  if ('013678lmnLMN'.includes(lastChar)) return JongsungType.YES;
  if ('2459aefhijkoqrsuvwxyzAEFHIJKOQRSUVWXYZ'.includes(lastChar)) {
    return JongsungType.YES;
  }

  if (code < 0xac00 || code > 0xd7a3) {
    return JongsungType.NO;
  }

  return (code - 0xac00) % 28 === 0 ? JongsungType.NO : JongsungType.YES;
};

/**
 * 한글 자소(초/중/종)성 분리.
 *
 * @param {String} text
 */
export const hangulToJaso = (text: string): string[] => {
  const ChoSeong: number[] = new Array(
    0x3131,
    0x3132,
    0x3134,
    0x3137,
    0x3138,
    0x3139,
    0x3141,
    0x3142,
    0x3143,
    0x3145,
    0x3146,
    0x3147,
    0x3148,
    0x3149,
    0x314a,
    0x314b,
    0x314c,
    0x314d,
    0x314e,
  );
  const JungSeong: number[] = new Array(
    0x314f,
    0x3150,
    0x3151,
    0x3152,
    0x3153,
    0x3154,
    0x3155,
    0x3156,
    0x3157,
    0x3158,
    0x3159,
    0x315a,
    0x315b,
    0x315c,
    0x315d,
    0x315e,
    0x315f,
    0x3160,
    0x3161,
    0x3162,
    0x3163,
  );
  const JongSeong: number[] = new Array(
    0x0000,
    0x3131,
    0x3132,
    0x3133,
    0x3134,
    0x3135,
    0x3136,
    0x3137,
    0x3139,
    0x313a,
    0x313b,
    0x313c,
    0x313d,
    0x313e,
    0x313f,
    0x3140,
    0x3141,
    0x3142,
    0x3144,
    0x3145,
    0x3146,
    0x3147,
    0x3148,
    0x314a,
    0x314b,
    0x314c,
    0x314d,
    0x314e,
  );
  const chars = new Array();
  const v: string[] = new Array();
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);

    chars[i] = text.charCodeAt(i);
    if (code >= 0xac00 && code <= 0xd7a3) {
      let i3 = chars[i] - 0xac00;

      const i1 = Math.floor(i3 / (21 * 28));
      i3 = i3 % (21 * 28);

      const i2 = Math.floor(i3 / 28);
      const i4 = (i3 = i3 % 28);
      v.push(String.fromCharCode(ChoSeong[i1]));
      v.push(String.fromCharCode(JungSeong[i2]));
      if (i4 !== 0) {
        v.push(String.fromCharCode(JongSeong[i3]));
      }
    } else {
      v.push(String.fromCharCode(chars[i]));
    }
  }
  return v;
};

/**
 * 한글 포함 여부 체크.
 * @param str 문자열
 * @returns 한글포함여부
 *
 * @example
 * isHangul('abc');     // false
 * isHangul('가');      // true
 * isHangul('한abc');   // true
 */
export const isHangul = (str: string) => {
  let rtnData = false;
  for (let idx = 0; idx < str.length; idx++) {
    const c = escape(str.charAt(idx));
    if (c.indexOf('%u') > -1) {
      rtnData = true;
      break;
    }
  }
  return rtnData;
};

/**
 * 전각문자로 변환
 * @param is 변환할 문자열
 * @param isAllNum 모든 문자가 숫자인지 여부
 * @returns 전각으로 변환된 문자열
 *
 * @example
 * toFullChar('abc');       // 'A B C'
 */
export const toFullChar = (is: string, isAllNum: boolean = false) => {
  if (!is) return;

  if (isAllNum && !isHangul(is)) return is;

  let os = '';
  for (let i = 0; i < is.length; i++) {
    const c = is.charCodeAt(i);
    if (32 <= c && c <= 126) {
      // 전각으로 변환 될 수 있는 문자의 범위.
      if (c == 32)
        // 스페이스인 경우 ascii 코드 32
        os = os + unescape('%u' + (12288).toString(16));
      else os = os + unescape('%u' + (c + 65248).toString(16));
    } else {
      os = os + is.charAt(i);
    }
  }
  return os;
};

/**
 * 전각을 반각으로 변환.
 *
 * @param is 입력 문장려
 * @returns 반격으로 변환된 문자열
 *
 * @example
 * toHalfChar('A B C');     // 'ABC'
 * toHalfChar('1 2 3');     // '123'
 */
export const toHalfChar = (is: string): string => {
  let os = '';
  let len = is.length;
  for (let i = 0; i < len; i++) {
    let c = is.charCodeAt(i);
    if (c >= 65281 && c <= 65374 && c != 65340) {
      os += String.fromCharCode(c - 65248);
    } else if (c == 8217) {
      os += String.fromCharCode(39);
    } else if (c == 8221) {
      os += String.fromCharCode(34);
    } else if (c == 12288) {
      os += String.fromCharCode(32);
    } else if (c == 65507) {
      os += String.fromCharCode(126);
    } else if (c == 65509) {
      os += String.fromCharCode(92);
    } else {
      os += is.charAt(i);
    }
  }
  return os;
};

/**
 * 문자열을 "UTF-8 바이트열을 담은 문자열" 형태로 인코딩한다.
 *
 * @param s 원본문자열
 * @returns UTF-8 바이트를 담은 문자열
 *
 * @example
 * const enc = toUtf8('가');
 * fromUtf8(enc);
 */
export const toUtf8 = (s: string) => {
  var c,
    d = '';
  for (var i = 0; i < s.length; i++) {
    c = s.charCodeAt(i);
    if (c <= 0x7f) {
      d += s.charAt(i);
    } else if (c >= 0x80 && c <= 0x7ff) {
      d += String.fromCharCode(((c >> 6) & 0x1f) | 0xc0);
      d += String.fromCharCode((c & 0x3f) | 0x80);
    } else {
      d += String.fromCharCode((c >> 12) | 0xe0);
      d += String.fromCharCode(((c >> 6) & 0x3f) | 0x80);
      d += String.fromCharCode((c & 0x3f) | 0x80);
    }
  }
  return d;
};

/**
 * toUtf8 함수로 만든 문자를 복원
 * @param s UTF-8 바이트 문자열
 * @returns 디코딩된 원문 문자열
 *
 * @example
 * const enc = toUtf8('가');
 * fromUtf8(enc); // '가'
 */
export const fromUtf8 = (s: string) => {
  let c,
    d = '',
    flag = 0,
    tmp: number = 0;
  for (var i = 0; i < s.length; i++) {
    c = s.charCodeAt(i);
    if (flag == 0) {
      if ((c & 0xe0) == 0xe0) {
        flag = 2;
        tmp = (c & 0x0f) << 12;
      } else if ((c & 0xc0) == 0xc0) {
        flag = 1;
        tmp = (c & 0x1f) << 6;
      } else if ((c & 0x80) == 0) {
        d += s.charAt(i);
      } else {
        flag = 0;
      }
    } else if (flag == 1) {
      flag = 0;
      d += String.fromCharCode(tmp | (c & 0x3f));
    } else if (flag == 2) {
      flag = 3;
      tmp |= (c & 0x3f) << 6;
    } else if (flag == 3) {
      flag = 0;
      d += String.fromCharCode(tmp | (c & 0x3f));
    } else {
      flag = 0;
    }
  }
  return d;
};

/**
 * 16진수 문자열(hex)을 문자열로 변환한다.
 * @param hex 16진수 문자열
 * @returns 티코딩된 문자열
 *
 * @example
 * hexToStr('48656c6c6f');      // 'Hello'
 * hexToStr('313233');          // '123'
 */
export const hexToStr = (hex: string) => {
  hex = hex.toString(); // force conversion
  var str = '';
  for (var i = 0; i < hex.length; i += 2)
    str += String.fromCharCode(parseInt(hex.substring(i, 2), 16));
  return str;
};

/**
 * delimiter 패턴에 맞춰 src 문자열을 포맷팅한다.
 *
 * @param src 원본 문자열
 * @param delimiter 패턴 구분자 ('9999-99-99')
 * @returns 포맷팅된 문자열
 *
 * @example
 * setDelim('20251231', '9999-99-99');      // '2025-12-31'
 * setDelim('123', '9999-99-99');           // '123'
 */
export const setDelim = (src: string, delimiter: string = ''): string => {
  if (typeof src == 'undefined' || src == '') {
    return '';
  }

  let k = 0;
  for (let i = 0; i < delimiter.length; i++) {
    if (delimiter.charAt(i) == '9') {
      k++;
    }
  }

  if (src.length != k) {
    return src;
  }

  let str = '';
  try {
    for (let i = 0, j = 0; i < delimiter.length; i++) {
      if (delimiter.charAt(i) == '9') {
        str += src.charAt(j);
        j++;
      } else {
        str += delimiter.charAt(i);
      }
    }
  } catch (e) {
    return '';
  }
  return str;
};

/**
 * 숫자앞 '0'추가
 * @param num
 * @param len
 * @returns
 */
export const addZero = (num: number | string, len: number) => {
  num = num + '';
  if (num.length == len) return num;

  for (var i = 0, result = num; i < len - num.length; i++) result = '0' + result;

  return result;
};

/**
 * input text byte length.
 *
 * @param {String} str
 * @param {Integer} maxbyte
 */
export const getInputByte = (str: string, maxByte: number): string => {
  let byteSize = 0;
  for (let i = 0; i < str.length; i++) {
    byteSize += strCharByte(escape(str.charAt(i)));

    if (maxByte && byteSize > maxByte) return str.substring(0, i);
  }
  return str;
};

/**
 * 한글/영문 바이트 체크.
 */
export const strCharByte = (char: string) => {
  if (char.substring(0, 2) == '%u') return char.substring(2, 4) == '00' ? 1 : 2;
  else if (char.substring(0, 1) == '%') return parseInt(char.substring(1, 3), 16) > 127 ? 2 : 1;
  else return 1;
};
